import * as React from "react";
import { Hint } from "react-ui";

import { PicklistColumnMeta, PicklistDataItem } from "../../../../../Engine/Core/Picklists/PicklistTypes";
import { Markdown } from "../../../../Helpers/Markdown/Markdown";
import { useAsyncEffect } from "../../../../../../../Common/UseAsyncEffect";
import { isNullOrEmpty } from "../../../../../../../Common/TypingUtils";
import { TreePicklistProps } from "../TreePicklist";
import { getPickListItemsByValue } from "../../Common/PicklistItemProvider";

export interface TreePicklistHintProps extends TreePicklistProps {
    hintOpened: boolean;
    hintWidth?: number;
    children: React.ReactElement;
}

export const TreePicklistHint = (props: TreePicklistHintProps): JSX.Element => {
    const [items, setItems] = React.useState<PicklistDataItem[]>([]);
    const [columnMetas, setColumnMetas] = React.useState<PicklistColumnMeta[]>([]);
    const hintFields = props.hintFields ?? [];

    useAsyncEffect(async () => {
        if (isNullOrEmpty(props.value)) {
            setItems([]);
            return;
        }
        const result = await getPickListItemsByValue(props.value, props);
        setItems(result.items);
        setColumnMetas(result.columnMetas.filter(meta => hintFields.some(fieldName => meta.name === fieldName)));
    }, [props.value]);

    if (items.length === 1) {
        return <Hint text={props.hintOpened ? items[0]?.name : undefined}>{props.children}</Hint>;
    }

    const maxLength = items.reduce((acc, item) => Math.max(acc, getItemLength(item, columnMetas)), 0);
    const hintText = items.map(item => getItemHint(item, columnMetas));

    const hintOpened = props.hintOpened && maxLength > 0;

    return <Hint text={hintOpened ? hintText : undefined}>{props.children}</Hint>;
};

const getItemLength = (item: PicklistDataItem, columnMetas: PicklistColumnMeta[]): number =>
    columnMetas.reduce((acc, column) => acc + (item[column.name]?.toString().length ?? 0), 0);

const getItemHint = (item: PicklistDataItem, columnMetas: PicklistColumnMeta[]): JSX.Element => {
    const cells = columnMetas
        .filter(column => !!item[column.name])
        .map(column => {
            const name = column.name;
            const value = item[name]?.toString() ?? "";

            return (
                <>
                    {column.withMarkdown ? (
                        <Markdown
                            components={{
                                a: ({ children }) => <span>{children}</span>,
                            }}>
                            {value}
                        </Markdown>
                    ) : (
                        value
                    )}
                </>
            );
        });

    return <>{cells}</>;
};
